import { useState, useMemo, useCallback, useEffect } from "react";
import { Plus } from "lucide-react";
import { Trans } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import { TableSkeleton } from "@/components/shared/TableSkeleton";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { PageContainer } from "@/components/layout/PageContainer";
import { PageHeader } from "@/components/layout/PageHeader";
import { useDocuments, useDeleteDocument } from "@/hooks/useDocuments";
import { DocumentsTable } from "@/components/documents/DocumentsTable";
import { CreateDocumentSheet } from "@/components/documents/CreateDocumentSheet";
import { EditDocumentSheet } from "@/components/documents/EditDocumentSheet";
import { useSearchParams } from "react-router";
import type { Document, DocumentsFilters } from "@/types/document";

export function DocumentsPage() {
  const [searchParams] = useSearchParams();

  // Fetch all documents with client-side filtering
  const [filters] = useState<DocumentsFilters>({
    page: 1,
    page_size: 1000,
  });

  // Sheet state
  const [createSheetOpen, setCreateSheetOpen] = useState(false);

  // Open create sheet when navigated with ?action=create
  useEffect(() => {
    if (searchParams.get("action") === "create") {
      setCreateSheetOpen(true);
    }
  }, [searchParams]);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);

  // Delete dialog state
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);

  // Query data
  const { data, isLoading, isError, error, refetch } = useDocuments(filters);
  const deleteMutation = useDeleteDocument();

  const documents = useMemo(() => data?.items ?? [], [data?.items]);

  const handleEdit = useCallback((doc: Document) => {
    setSelectedDocument(doc);
    setEditSheetOpen(true);
  }, []);

  const handleEditSheetClose = useCallback(() => {
    setEditSheetOpen(false);
    setSelectedDocument(null);
  }, []);

  const handleDeleteRequest = useCallback((doc: Document) => {
    setEditSheetOpen(false);
    setSelectedDocument(null);
    setDocumentToDelete(doc);
  }, []);

  // `documentToDelete` is a persisted record from the list; the generated model
  // marks `id` optional. A rejection keeps the dialog open; the hook toasts.
  const handleDeleteConfirm = () => deleteMutation.mutateAsync(documentToDelete!.id!);

  return (
    <PageContainer width="list" className="flex h-full flex-col">
      <PageHeader
        className="flex-none"
        title={<Trans id="nav.documents">Documents</Trans>}
        actions={
          <Button
            onClick={() => setCreateSheetOpen(true)}
            className="flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            <span>
              <Trans id="documents.create.button">New Document</Trans>
            </span>
          </Button>
        }
      />

      {/* Table. Loading and error render in-place here so the header and action
          bar above stay mounted. */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <TableSkeleton columns={5} />
        ) : isError ? (
          <QueryErrorState
            error={error}
            onRetry={() => void refetch()}
            title={<Trans id="documents.error.loadFailed">Failed to load documents</Trans>}
          />
        ) : (
          <DocumentsTable
            documents={documents}
            onEdit={handleEdit}
            onAdd={() => setCreateSheetOpen(true)}
          />
        )}
      </div>

      {/* Create Sheet */}
      <CreateDocumentSheet
        open={createSheetOpen}
        onOpenChange={setCreateSheetOpen}
      />

      {/* Edit Sheet */}
      <EditDocumentSheet
        open={editSheetOpen}
        onOpenChange={handleEditSheetClose}
        document={selectedDocument}
        onDelete={handleDeleteRequest}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={documentToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setDocumentToDelete(null);
        }}
        title={<Trans id="documents.delete.title">Delete Document</Trans>}
        description={
          <Trans id="documents.delete.confirmation">
            Are you sure you want to delete this document? This action cannot
            be undone.
          </Trans>
        }
        confirmLabel={<Trans id="common.delete">Delete</Trans>}
        variant="destructive"
        isPending={deleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
      />
    </PageContainer>
  );
}
